// ============================================
// Material Reflection — the WGSL `@material {...}` marker
// ============================================
//
// A material-capable shader declares its authoring surface in a JSON block
// inside a comment at the top of the .wgsl:
//
//   /* @material {
//     "supports": ["sprite", "text", "vfx"],
//     "tag": "transparent",
//     "properties": [
//       { "name": "tint",  "type": "color", "default": [1,1,1,1], "label": "Tint" },
//       { "name": "speed", "type": "f32",   "default": 1, "min": 0, "max": 10 }
//     ],
//     "overrides": [ { "name": "USE_GLOW", "type": "bool", "default": false } ],
//     "textures": [ { "slot": "ALBEDO", "label": "Albedo" } ]
//   } */
//
// This marker is the SINGLE source of reflection truth (materials epic, goals 7 + 12):
//  - `properties` → the typed uniform struct (see `buildUniformDef`)
//  - `overrides`  → WGSL `override`-constant toggles (the variant mechanism)
//  - `textures`   → named texture-slot pickers
//  - `supports`   → which renderers may use this material
//
// naga still validates the actual WGSL; this marker only drives authoring/UI. A
// .wgsl with no marker is a plain engine shader (not material-assignable).

import { GPUType, defineStruct, type GPUTypeInfo, type StructDef, type GPUValue } from '../core/GPUTypes';
import { type TextureSlotName, type MaterialTag } from '../types';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/** Authoring type of a material property (maps to a GPU uniform field). */
export type MaterialPropertyType = 'color' | 'f32' | 'vec2' | 'vec3' | 'vec4' | 'i32';

/** Authoring type of a shader `override` constant. */
export type MaterialOverrideType = 'bool' | 'f32' | 'i32' | 'u32';

/** A renderer that can consume a material (open set; built-ins: sprite/text/vfx). */
export type MaterialRendererKind = 'sprite' | 'text' | 'vfx' | (string & {});

/** One uniform-backed material property exposed to the inspector. */
export interface MaterialProperty {
  name: string;
  type: MaterialPropertyType;
  /** Authoring default — number for scalars, number[] for vectors/color. */
  default?: number | number[];
  label?: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  group?: string;
}

/** One WGSL `override` constant exposed as a variant toggle. */
export interface MaterialOverride {
  name: string;
  type: MaterialOverrideType;
  default?: number | boolean;
  label?: string;
  /** Optional enum choices (for dropdown rendering). */
  options?: Array<{ label: string; value: number | boolean }>;
  /**
   * Spec-constant numeric id (`@id(N)`), when known. Slang emits `@id(N) override NAME_N` — the WGSL
   * identifier is mangled, so pipeline constants must be keyed by this id (WebGPU accepts `"N"`), not the
   * authored name. Undefined for plain-WGSL `override NAME` (no `@id`) → keyed by name.
   */
  id?: number;
}

/** One named texture slot the material binds. */
export interface MaterialTextureSlotDef {
  slot: TextureSlotName;
  label?: string;
}

/** Reflected material surface (from the shader's WGSL). */
export interface MaterialReflection {
  supports: MaterialRendererKind[];
  tag?: MaterialTag;
  properties: MaterialProperty[];
  overrides: MaterialOverride[];
  textures: MaterialTextureSlotDef[];
  /** Engine capabilities the shader opts into via `//! requires:` (e.g. 'time', 'lighting'). */
  requires: string[];
  /** Shorthand: `requires` includes 'time' (or the struct has a `time` field) → engine feeds `uTime`. */
  usesTime: boolean;
  /** Shorthand: `requires` includes 'lighting' → pipeline gets the group(1) lighting bind group. */
  usesLighting: boolean;
}

// ----------------------------------------------------------------------------
// Reflection from WGSL — the shader's real code IS the schema (no duplicated marker)
// ----------------------------------------------------------------------------
//
// A shader opts into being a material with ONE line:  `//! supports: sprite[, text, vfx]`
// Everything else is reflected from the actual WGSL:
//   - the `@group(0) @binding(0) var<uniform>` struct's fields (after viewProjection) → properties
//   - `override` declarations → variants (native WGSL name / type / default)
//   - `texture_2d` `var` bindings (in declared order) → texture slots (ALBEDO/NORMAL/EMISSION/MASK)
// Optional terse `//!` hints on a struct field refine the inspector UI:
//   tint:  vec4<f32>,   //! color
//   speed: f32,         //! range(0, 10) label "Speed" default 1
//   tag:                //! tag: transparent   (line on its own, optional; default transparent)

// `//!` directives must START a line (after optional indentation) — so a mention of `//! supports:` inside
// prose/another comment is NOT mistaken for the opt-in. (`m` flag → `^` matches each line start.)
const SUPPORTS_RE = /^[ \t]*\/\/!\s*supports\s*:?\s*(.+)$/im;
const TAG_RE = /^[ \t]*\/\/!\s*tag\s*:?\s*(opaque|transparent|overlay)/im;
const REQUIRES_RE = /^[ \t]*\/\/!\s*requires\s*:?\s*(.+)$/im;
const UNIFORM_VAR_RE = /@group\(0\)\s*@binding\(0\)\s*var\s*<\s*uniform\s*>\s*\w+\s*:\s*(\w+)/;
const OVERRIDE_RE = /\boverride\s+(\w+)\s*:\s*(bool|f32|i32|u32)\s*(?:=\s*([^;]+))?;/g;
const TEXTURE_RE = /@group\(0\)\s*@binding\(\d+\)\s*var\s+(\w+)\s*:\s*texture_2d\b/g;
const FIELD_RE = /^(\w+)\s*:\s*([A-Za-z0-9_<>]+)\s*,?\s*(?:\/\/!\s*(.*))?$/;
const TEXTURE_SLOTS: readonly TextureSlotName[] = ['ALBEDO', 'NORMAL', 'EMISSION', 'MASK'];

/** True if a shader opts into being a material (has a `//! supports:` line). */
export function isMaterialShader(source: string): boolean {
  return SUPPORTS_RE.test(source);
}

/**
 * Reflect a shader's material surface directly from its WGSL. Returns null if the shader is not a
 * material (no `//! supports:` opt-in line). Never throws — malformed code yields a partial reflection.
 */
export function reflectWgsl(source: string): MaterialReflection | null {
  const supportsM = SUPPORTS_RE.exec(source);
  if (!supportsM) return null;
  const supports = supportsM[1].split(',').map((s) => s.trim()).filter(Boolean);
  const tagM = TAG_RE.exec(source);
  const requiresM = REQUIRES_RE.exec(source);
  const requires = requiresM ? requiresM[1].split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) : [];
  const { properties, usesTime: structTime } = reflectUniformStruct(source);
  return {
    supports,
    tag: tagM ? (tagM[1].toLowerCase() as MaterialTag) : undefined,
    properties,
    overrides: reflectOverrides(source),
    textures: reflectTextures(source),
    requires,
    // `time` opts in via the `requires` flag OR a bare `time` field in the struct (both keep the layout right).
    usesTime: requires.includes('time') || structTime,
    usesLighting: requires.includes('lighting'),
  };
}

/**
 * Parse the binding(0) uniform struct: its fields → properties, skipping the engine-provided
 * `viewProjection` (mat4) and `time` (f32) — the latter flips `usesTime` so an animated shader gets a
 * `uTime` uniform fed each frame.
 */
function reflectUniformStruct(source: string): { properties: MaterialProperty[]; usesTime: boolean } {
  const varM = UNIFORM_VAR_RE.exec(source);
  if (!varM) return { properties: [], usesTime: false };
  const body = extractStructBody(source, varM[1]);
  if (body == null) return { properties: [], usesTime: false };

  const properties: MaterialProperty[] = [];
  let usesTime = false;
  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('//')) continue;
    const fm = FIELD_RE.exec(line);
    if (!fm) continue;
    const [, name, wgslType, hintStr] = fm;
    if (/^u?viewProjection$/i.test(name)) continue; // engine-provided
    if (/^u?time$/i.test(name)) { usesTime = true; continue; } // engine-provided (animation clock)
    const hints = parseHints(hintStr ?? '');
    const type = wgslTypeToPropertyType(wgslType, hints.color);
    if (!type) continue; // unsupported uniform type (e.g. mat4x4) — skip
    properties.push({ name, type, default: hints.default, label: hints.label, min: hints.min, max: hints.max });
  }
  return { properties, usesTime };
}

function reflectOverrides(source: string): MaterialOverride[] {
  const out: MaterialOverride[] = [];
  OVERRIDE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = OVERRIDE_RE.exec(source)) !== null) {
    const [, name, type, rawDefault] = m;
    let def: number | boolean | undefined;
    if (rawDefault !== undefined) {
      const d = rawDefault.trim();
      def = d === 'true' ? true : d === 'false' ? false : Number(d);
      if (typeof def === 'number' && Number.isNaN(def)) def = undefined;
    }
    out.push({ name, type: type as MaterialOverrideType, default: def });
  }
  return out;
}

function reflectTextures(source: string): MaterialTextureSlotDef[] {
  const out: MaterialTextureSlotDef[] = [];
  TEXTURE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = TEXTURE_RE.exec(source)) !== null && i < TEXTURE_SLOTS.length) {
    out.push({ slot: TEXTURE_SLOTS[i], label: m[1] });
    i++;
  }
  return out;
}

/** Extract the brace-balanced body of `struct NAME { ... }`. */
function extractStructBody(source: string, name: string): string | null {
  const m = new RegExp(`struct\\s+${name}\\s*\\{`).exec(source);
  if (!m) return null;
  const start = m.index + m[0].length;
  let depth = 0;
  for (let i = start; i < source.length; i++) {
    const c = source[i];
    if (c === '{') depth++;
    else if (c === '}') {
      if (depth === 0) return source.slice(start, i);
      depth--;
    }
  }
  return null;
}

function wgslTypeToPropertyType(wgsl: string, colorHint: boolean): MaterialPropertyType | null {
  const t = wgsl.replace(/\s/g, '');
  if (t === 'vec4<f32>' || t === 'vec4f') return colorHint ? 'color' : 'vec4';
  if (t === 'vec3<f32>' || t === 'vec3f') return 'vec3';
  if (t === 'vec2<f32>' || t === 'vec2f') return 'vec2';
  if (t === 'f32') return 'f32';
  if (t === 'i32') return 'i32';
  return null;
}

interface FieldHints {
  color: boolean;
  label?: string;
  min?: number;
  max?: number;
  default?: number | number[];
}

/** Parse a struct field's terse `//!` hints: `color`, `range(a,b)`, `label "x"`, `default(...)` / `default x`. */
function parseHints(s: string): FieldHints {
  const hints: FieldHints = { color: /\bcolor\b/.test(s) };
  const range = /range\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)/.exec(s);
  if (range) { hints.min = Number(range[1]); hints.max = Number(range[2]); }
  const label = /label\s+"([^"]+)"/.exec(s) ?? /label\s+(\w+)/.exec(s);
  if (label) hints.label = label[1];
  const defTuple = /default\s*\(\s*([^)]+)\)/.exec(s);
  if (defTuple) hints.default = defTuple[1].split(',').map((x) => Number(x.trim()));
  else {
    const defScalar = /default\s+(-?[\d.]+)/.exec(s);
    if (defScalar) hints.default = Number(defScalar[1]);
  }
  return hints;
}

// ----------------------------------------------------------------------------
// Uniform struct from reflection
// ----------------------------------------------------------------------------

/** Map an authoring property type to its GPU uniform type. `color` is a vec4<f32>. */
export function propertyTypeToGPUType(type: MaterialPropertyType): GPUTypeInfo {
  switch (type) {
    case 'color':
    case 'vec4':
      return GPUType.f32().vec4();
    case 'vec3':
      return GPUType.f32().vec3();
    case 'vec2':
      return GPUType.f32().vec2();
    case 'i32':
      return GPUType.i32().build();
    case 'f32':
    default:
      return GPUType.f32().build();
  }
}

/**
 * Build the typed uniform `StructDef` for a material's OWN properties, or null when
 * the material declares no properties. Field names == property names, so the
 * .mat's stored property values map 1:1 onto `Material.setProperties(...)`. This is
 * the material's authored surface, independent of any renderer.
 */
export function buildUniformDef(
  reflection: MaterialReflection,
  structName = 'MaterialUniforms',
): StructDef<Record<string, GPUTypeInfo>> | null {
  if (reflection.properties.length === 0) return null;
  const fields: Record<string, GPUTypeInfo> = {};
  for (const p of reflection.properties) {
    fields[p.name] = propertyTypeToGPUType(p.type);
  }
  return defineStruct(structName, fields);
}

/**
 * Build the uniform `StructDef` for a material rendered through the **SpriteBatch**
 * (sprite + text + the editor preview). The sprite batch uploads the view-projection
 * via `ctx.setUniform('uViewProjection', ...)`, which only resolves if the pipeline's
 * uniform layout has a field named `uViewProjection` at offset 0 — so this struct is
 * `{ uViewProjection: mat4x4<f32>, ...properties }`.
 *
 * The material's WGSL MUST declare a matching `@group(0) @binding(0)` uniform struct whose first member is
 * the view-projection (mat4x4), then (if animated) `time: f32`, then the properties in the same order, plus
 * `@binding(1) sampler` + `@binding(2) texture_2d` if it samples (matching `shaders/sprite.wgsl`). Always
 * returns a struct (≥ uViewProjection). `uTime` is included only when the shader declares a `time` field, so
 * the struct layout stays in lockstep with the WGSL (no offset drift for non-animated materials).
 */
export function buildSpriteUniformDef(
  reflection: MaterialReflection,
  structName = 'SpriteMaterialUniforms',
): StructDef<Record<string, GPUTypeInfo>> {
  const fields: Record<string, GPUTypeInfo> = { uViewProjection: GPUType.f32().mat4x4() };
  if (reflection.usesTime) fields.uTime = GPUType.f32().build();
  for (const p of reflection.properties) {
    fields[p.name] = propertyTypeToGPUType(p.type);
  }
  return defineStruct(structName, fields);
}

// ----------------------------------------------------------------------------
// Defaults
// ----------------------------------------------------------------------------

/** Zero/identity default value for a property type when no explicit default is given. */
function defaultValueFor(p: MaterialProperty): GPUValue {
  if (p.default !== undefined) return p.default;
  switch (p.type) {
    case 'color':
      return [1, 1, 1, 1];
    case 'vec4':
      return [0, 0, 0, 0];
    case 'vec3':
      return [0, 0, 0];
    case 'vec2':
      return [0, 0];
    default:
      return 0;
  }
}

/** Default override value (false for bool, 0 for numeric) when none is declared. */
function defaultOverrideValue(o: MaterialOverride): number | boolean {
  if (o.default !== undefined) return o.default;
  return o.type === 'bool' ? false : 0;
}

/** Full default property/override sets for a freshly-created material of this shader. */
export function materialDefaults(reflection: MaterialReflection): {
  properties: Record<string, GPUValue>;
  overrides: Record<string, number | boolean>;
} {
  const properties: Record<string, GPUValue> = {};
  for (const p of reflection.properties) properties[p.name] = defaultValueFor(p);
  const overrides: Record<string, number | boolean> = {};
  for (const o of reflection.overrides) overrides[o.name] = defaultOverrideValue(o);
  return { properties, overrides };
}
